import type { AccessLevel, AccessPolicy, JobRole, TenantRole } from './types';

/**
 * The three access levels people see. Mirrors app/shared/src/roles.ts:
 *   Super User  tenant role org_admin — everything in the organization
 *   PM Admin    job role admin        — everything on their jobs, including sending texts
 *   PM User     job role user         — everything on their jobs except sending texts
 */
export const ACCESS_LEVELS: AccessLevel[] = ['super_user', 'pm_admin', 'pm_user'];

export const LEVEL_LABEL: Record<AccessLevel, string> = { super_user: 'Super User', pm_admin: 'PM Admin', pm_user: 'PM User' };

export const LEVEL_DESCRIPTION: Record<AccessLevel, string> = {
  super_user: 'Runs the whole organization: users, every job, settings. Can send texts on any job.',
  pm_admin: 'Runs the jobs they are assigned to, including sending texts and managing the team.',
  pm_user: 'Runs the jobs they are assigned to (contacts, keywords, automation, settings) but cannot send texts.',
};

export function levelOf(tenantRole: TenantRole, jobRole?: JobRole | null): AccessLevel {
  if (tenantRole === 'org_admin') return 'super_user';
  return jobRole === 'admin' ? 'pm_admin' : 'pm_user';
}

export const levelForJobRole = (role: JobRole): AccessLevel => (role === 'admin' ? 'pm_admin' : 'pm_user');
export const jobRoleForLevel = (level: AccessLevel): JobRole => (level === 'pm_admin' ? 'admin' : 'user');

/** Highest level a person holds: Super User, else PM Admin if they may send anywhere, else PM User. */
export function highestLevel(tenantRole: TenantRole, jobs: Array<{ role: JobRole | null }>): AccessLevel {
  if (tenantRole === 'org_admin') return 'super_user';
  return jobs.some((j) => j.role === 'admin') ? 'pm_admin' : 'pm_user';
}

export type CapabilityCell = 'yes' | 'no' | keyof AccessPolicy;

export interface Capability {
  key: string;
  label: string;
  hint?: string;
  super_user: CapabilityCell;
  pm_admin: CapabilityCell;
  pm_user: CapabilityCell;
}

/** What each level can do. A policy key means "on, unless the organization switched it off". */
export const CAPABILITIES: Capability[] = [
  { key: 'send', label: 'Send texts', hint: 'Compose, schedule, send drafts, cancel scheduled sends.', super_user: 'yes', pm_admin: 'yes', pm_user: 'no' },
  { key: 'grant', label: 'Make someone a PM Admin', hint: 'Grant or remove sending rights on a job.', super_user: 'yes', pm_admin: 'pmAdminManageTeam', pm_user: 'no' },
  { key: 'team', label: 'Manage the job team', hint: 'Add, invite and remove people. PM Users can only add or remove other PM Users.', super_user: 'yes', pm_admin: 'pmAdminManageTeam', pm_user: 'pmUserManageTeam' },
  { key: 'contacts', label: 'Contacts', hint: 'Add, import, edit, tag and export contacts; view history and reports.', super_user: 'yes', pm_admin: 'yes', pm_user: 'yes' },
  { key: 'settings', label: 'Job settings and site address', hint: 'Name, description, sender label, address, timezone, quiet hours.', super_user: 'yes', pm_admin: 'pmAdminEditJob', pm_user: 'pmUserEditJob' },
  { key: 'keywords', label: 'Keywords and auto-replies', hint: 'When the organization allows job-level keywords.', super_user: 'yes', pm_admin: 'yes', pm_user: 'pmUserManageKeywords' },
  { key: 'automation', label: 'Automation rules that create drafts', super_user: 'yes', pm_admin: 'yes', pm_user: 'pmUserManageAutomation' },
  { key: 'autosend', label: 'Automation rules that send automatically', super_user: 'yes', pm_admin: 'pmAdminAutoSend', pm_user: 'no' },
  { key: 'jobs', label: 'Create and archive jobs, assign phone numbers', super_user: 'yes', pm_admin: 'no', pm_user: 'no' },
  { key: 'users', label: 'Invite people and change access levels', super_user: 'yes', pm_admin: 'no', pm_user: 'no' },
  { key: 'org', label: 'Organization settings and branding', super_user: 'yes', pm_admin: 'no', pm_user: 'no' },
];

export const POLICY_LABEL: Record<keyof AccessPolicy, { label: string; hint: string; level: AccessLevel }> = {
  pmAdminManageTeam: { label: 'PM Admins manage their job team', hint: 'Add, invite and remove people on their jobs, including making others PM Admins.', level: 'pm_admin' },
  pmAdminEditJob: { label: 'PM Admins change job settings', hint: 'Name, description, sender label, site address and timezone.', level: 'pm_admin' },
  pmAdminAutoSend: { label: 'PM Admins create auto-sending rules', hint: 'Weather and schedule rules that send without review. Off means drafts only.', level: 'pm_admin' },
  pmUserManageTeam: { label: 'PM Users manage other PM Users', hint: 'Add, invite and remove PM Users on their jobs. They can never touch PM Admins.', level: 'pm_user' },
  pmUserEditJob: { label: 'PM Users change job settings', hint: 'Name, description, sender label, site address and timezone.', level: 'pm_user' },
  pmUserManageKeywords: { label: 'PM Users manage keywords', hint: 'Keywords and auto-replies on their jobs, when job keywords are allowed.', level: 'pm_user' },
  pmUserManageAutomation: { label: 'PM Users manage draft automation', hint: 'Create and edit rules that create drafts for a PM Admin to send.', level: 'pm_user' },
};

/** Resolve a matrix cell against the organization's policy. */
export function cellOn(cell: CapabilityCell, policy: AccessPolicy | undefined): boolean {
  if (cell === 'yes') return true;
  if (cell === 'no') return false;
  return policy?.[cell] ?? true;
}
